/**
 * cardSpine — the shared spine every ProjectionCard variant composes (PR-C1
 * §5). This is the pattern Contractor B's B2 fan-out replicates: B picks a
 * variant, it does NOT improvise a card.
 *
 * The spine supplies, uniformly across all variants:
 *   - the card shell (title + row list) — `ProjectionCard`;
 *   - the per-row frame with the provenance chip + status pill slot + the
 *     inline-edit and archive affordances — `ProjectionRow`;
 *   - the LIVE edit/archive wiring (FE-integration pass; this replaced
 *     PR-C1's stubbedProjectionWrite seam): edit opens the registry-derived
 *     RowEditDialog; archive/restore PATCH the #408 direct-edit routes;
 *     every success invalidates the ['clientRecord'] aggregate (one
 *     audited re-read) — never a raw insert/update, never a cache patch.
 *   - Rows WITHOUT an `edit` config render NO affordances, just a quiet
 *     hint — a projection with no direct-edit route AND no archive route.
 *     A row whose `edit` carries a projection but no `values`/`label`
 *     (hospital events) renders Archive/Restore with no Edit button:
 *     content still rides stewardship notes, but a wrong or duplicate
 *     row can be removed directly.
 *
 * Tokens only (sage / sand / ink-soft / warm). No raw greys or hand-rolled
 * button chrome — edit/archive compose the Button primitive.
 */

import { useSupportContact } from '@/hooks/useSupportContact';
import { createContext, useCallback, useContext, useEffect, useId, useState, type ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Pencil, Plus } from 'lucide-react';
import { CONFIRM_LANDED_EVENT, type ConfirmLandedDetail } from '@/lib/gaspSummary';
import { useTenant } from '@/lib/auth';
import { canWriteClinicalRecord } from '@/lib/capabilities';
import { Button } from '@/components/primitives/Button';
import { Chip, type ChipVariant } from '@/components/primitives/Chip';
import { ProvenanceChip } from '@/components/clientRecord/ProvenanceChip';
import { RowEditDialog } from '@/components/clientRecord/RowEditDialog';
import {
  RECORD_EDIT_PROJECTIONS,
  patchRecordRow,
  recordEditErrorMessage,
  type RecordAddProjection,
  type RecordEditProjection,
} from '@/lib/clientRecordEditApi';
import { PROPOSAL_PAYLOAD_SPECS, editableFields } from '@/lib/noteVocabulary';
import { humanizeToken } from '@/lib/fieldLabels';
import { recordDialogTitle } from '@/lib/recordFormWords';
import type { Provenance, DerivedProvenance, RowEnvelope } from '@/lib/clientRecord';
import { CARD_OPEN_KEY_PREFIX } from '@/lib/keptAcrossSignOut';

// ---------------------------------------------------------------------
// humanize — turn an enum token into a display label. Shared by every
// variant (so B's fan-out inherits ONE humanizer, not a local per-card
// copy). The acronym/product-label special-casing that used to live here
// (medicare_a, er_visit, dpoa, …) moved into the ONE shared helper —
// @/lib/fieldLabels (WS-8 caps sweep) — so every label surface derives
// identically. Re-exported so the variant call sites read unchanged.
// ---------------------------------------------------------------------
export const humanize = humanizeToken;

// ---------------------------------------------------------------------
// The live edit/archive seam (FE-integration pass — the stub swap).
// ---------------------------------------------------------------------

/** snake_case registry key → the snapshot row's camelCase field. */
function camelize(key: string): string {
  return key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/**
 * A snapshot row's current values re-keyed onto the CANONICAL registry
 * keys (snake_case) for the projection's editable fields — the shape the
 * #408 edit body and the RowEditDialog prefill expect. Mechanical
 * camelCase↔snake_case: the FE contract's field names are exactly the
 * registry keys camelized (startedOn ↔ started_on).
 */
export function registryValuesFromRow(
  row: Record<string, unknown>,
  projection: RecordEditProjection,
): Record<string, unknown> {
  const spec = PROPOSAL_PAYLOAD_SPECS[RECORD_EDIT_PROJECTIONS[projection].specType];
  const out: Record<string, unknown> = {};
  // directoryOnly fields (provider specialty/organization) never prefill:
  // they render from the linked Directory entry, not the row (WS-5).
  for (const [key, fs] of editableFields(spec)) {
    if (fs.directoryOnly) continue;
    const v = row[camelize(key)];
    // An address of five parts (0226) opens the form on its parts; with
    // no street it is text only and opens as on file.
    if (fs.kind === 'postal_address') {
      const c = camelize(key);
      const line1 = row[`${c}Line1`];
      if (typeof line1 === 'string') {
        const part = (k: string): string | null => (typeof row[`${c}${k}`] === 'string' ? (row[`${c}${k}`] as string) : null);
        out[key] = { line1, line2: part('Line2'), city: part('City'), state: part('State'), zip: part('Zip') };
        continue;
      }
    }
    if (v !== undefined) out[key] = v;
  }
  // A stay's facility Directory organization (LP-19): the edit opens on
  // it as the facility box's pick.
  if (projection === 'hospital-events' && typeof row['facilityOrgId'] === 'string') {
    out['facility_org_id'] = row['facilityOrgId'];
  }
  // A contact's Directory link and the entry's values (0227): the form
  // shows the entry and falls back to its phone, email and address.
  if (projection === 'contacts') {
    for (const key of CONTACT_DIRECTORY_VALUE_KEYS) {
      const v = row[camelize(key)];
      if (v !== undefined) out[key] = v;
    }
  }
  return out;
}

/** The contact row's Directory keys the edit form reads (0227). */
const CONTACT_DIRECTORY_VALUE_KEYS = [
  'directory_individual_id',
  'directory_org_id',
  'directory_name',
  'directory_phone',
  'directory_email',
  'directory_address',
  'directory_address_line1',
  'directory_address_line2',
  'directory_address_city',
  'directory_address_state',
  'directory_address_zip',
] as const;

/**
 * A row's live edit/archive wiring. ABSENT on a ProjectionRow = the row
 * has no direct-edit route AND no archive route (read-only) — no
 * affordances render at all.
 *
 * `values` PRESENT = a full direct-edit route: Edit + Archive both
 * render, and the dialog's title is the projection's ("Edit
 * medication"), never the row's value. ABSENT (projection only) =
 * Archive/Restore renders, Edit does not: the projection token alone is
 * enough to PATCH archive/restore, which never touches field content.
 */
export interface RowEditConfig {
  /** The #408 projection path token — always required (drives Archive
   *  regardless of whether this row also has a direct-edit route). */
  projection: RecordEditProjection;
  /** Current canonical registry-keyed values (registryValuesFromRow).
   *  Absent = no direct-edit route (Edit does not render). */
  values?: Record<string, unknown>;
  /** Passed through to the edit dialog (a linked provider's line, its
   *  Directory link, the Directory's values as placeholders, a field's help). */
  intro?: string;
  introLink?: { text: string; to: string };
  placeholders?: Readonly<Record<string, string>>;
  fieldHelp?: Readonly<Record<string, string>>;
  emptyValue?: Readonly<Record<string, string>>;
}

/** True when a row's edit config carries a real direct-edit route (Edit
 *  renders) rather than just the projection token for Archive/Restore. */
function hasEditRoute(edit: RowEditConfig): edit is RowEditConfig & { values: Record<string, unknown> } {
  return edit.values !== undefined;
}

// ---------------------------------------------------------------------
// The card shell — title + optional subtitle + the row list (or empty state).
// ---------------------------------------------------------------------
/**
 * CardAccent — the founder-approved domain-color system (UI polish pass):
 * every clinical/logistics card carries a slim colored LEFT BORDER band
 * + a heading tinted in the section's deep hue, matching the
 * Latest-Activity category tags exactly (color = domain, everywhere).
 * `band` is a `border-l-*` Tailwind class; `heading` is a `text-*` class
 * applied to the card's <h3>. Both are token-only (no raw hex/greys).
 * See CARD_ACCENTS below for the canonical per-domain set.
 */
export interface CardAccent {
  band: string;
  heading: string;
}

/**
 * The canonical domain → accent map (founder ruling, UI polish pass):
 * hues match the Overview "Latest activity" category tags 1:1.
 *   - medications              → amber   (border-l-warm-200 / text-warm-700)
 *   - appointments             → mist    (border-l-mist / text-mist-deep)
 *   - conditions, hospital &
 *     rehab, functional status → memory  (border-l-heather / text-heather-deep)
 *   - risk flags               → risk    (border-l-pepper-soft / text-pepper-deep,
 *                                          the risk register — stays the strongest)
 *   - legal & wishes, insurance,
 *     housing, addresses       → logistics (border-l-sage-300 / text-sage-700,
 *                                          one quiet sage family for the whole tab)
 * Allergies is hand-rolled (its safety states are richer than a plain
 * card) and carries its own softened terracotta treatment in-file
 * (cardVariants.tsx) rather than through this map.
 */
export const CARD_ACCENTS = {
  medications: { band: 'border-l-warm-200', heading: 'text-warm-700' },
  appointments: { band: 'border-l-mist', heading: 'text-mist-deep' },
  memory: { band: 'border-l-heather', heading: 'text-heather-deep' },
  risk: { band: 'border-l-pepper-soft', heading: 'text-pepper-deep' },
  logistics: { band: 'border-l-sage-300', heading: 'text-sage-700' },
} as const satisfies Record<string, CardAccent>;

export interface ProjectionCardProps {
  title: string;
  /** Empty-state copy when there are no rows. */
  emptyLabel?: string;
  /** Header action slot (record-add slice: the "+ Add" affordance),
   *  right-aligned next to the title. */
  action?: ReactNode;
  /** 'list' (default) wraps children in the row <ul>; 'block' renders
   *  them in a plain <div> (the functional-status table). */
  flow?: 'list' | 'block';
  /** Optional DOM id on the card shell — a scroll anchor for the G4
   *  hash-scroll primitive (e.g. id="risk-flags" for the top-bar risk
   *  link and the AttentionBand jump-to-risk). */
  id?: string;
  /** First-visit collapse state only (care-record restructure): the
   *  per-card localStorage persistence still wins after the user has
   *  ever toggled. Default false (open). */
  defaultCollapsed?: boolean;
  /** Collapsed-header count so a collapsed card still reads
   *  "Conditions · 20" — a collapse never hides that records exist. */
  collapsedCount?: number;
  /** The page is showing archived rows and this card holds some
   *  (useArchivedReveal's `showing`): the card opens so they are seen. */
  archivedShown?: boolean;
  /** The domain color accent (UI polish pass, PART 4) — a slim left
   *  border band + a tinted heading. Absent = the plain sage-800 heading,
   *  no band (People/providers/coverage-of-record cards not in the
   *  founder's named domain set). */
  accent?: CardAccent;
  children: ReactNode;
  testId?: string;
}

/**
 * B6 signature motion (Design Pass 2): when a confirm lands and this
 * section is the destination, the card acknowledges it with one gentle
 * 400ms pulse (class-driven; motion-reduce renders it inert). The queue
 * dispatches CONFIRM_LANDED_EVENT with the destination card's testid.
 */
function useConfirmLandedPulse(testId: string | undefined): boolean {
  const [pulsing, setPulsing] = useState(false);
  useEffect(() => {
    if (testId === undefined) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    function onLanded(e: Event): void {
      const detail = (e as CustomEvent<ConfirmLandedDetail>).detail;
      if (detail?.cardTestId !== testId) return;
      setPulsing(true);
      clearTimeout(timer);
      timer = setTimeout(() => setPulsing(false), 450);
    }
    window.addEventListener(CONFIRM_LANDED_EVENT, onLanded);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(CONFIRM_LANDED_EVENT, onLanded);
    };
  }, [testId]);
  return pulsing;
}

/**
 * G5 (Soak #2): a filled card collapses/expands, default open, the choice
 * persisted per card (keyed on its testId) so a CM's layout preferences
 * survive a reload. A card with no testId keeps an in-memory-only toggle.
 *
 * Care-record restructure: exported (the AllergiesCard hand-rolled
 * section composes the SAME collapse machinery — one mechanism, never a
 * local copy), and `defaultCollapsed` sets only the FIRST-VISIT state —
 * once the user has toggled, the persisted choice wins.
 *
 * `archivedShown` (LP-40): when the page's archived line turns to Show
 * and this card holds archived rows, the card opens so they are seen.
 * Only for the moment: the person's stored choice is not changed, and
 * they can fold the card again.
 */
export function usePersistedCardOpen(
  testId: string | undefined,
  id: string | undefined,
  defaultCollapsed = false,
  archivedShown = false,
): { open: boolean; toggle: () => void; openCard: () => void } {
  const { hash, key: locationKey } = useLocation();
  const key = testId ? `${CARD_OPEN_KEY_PREFIX}${testId}` : null;
  const [open, setOpen] = useState<boolean>(() => {
    if (key === null) return !defaultCollapsed;
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === 'closed') return false;
      if (stored === 'open') return true;
      return !defaultCollapsed;
    } catch {
      return !defaultCollapsed;
    }
  });
  // Force-open + persist — shared by the hash consumer below and the
  // open-on-add seam (CardOpenContext): a card must never hide the row a
  // CM just jumped to or just added.
  const openCard = (): void => {
    setOpen(true);
    if (key !== null) {
      try {
        window.localStorage.setItem(key, 'open');
      } catch {
        /* localStorage unavailable — state stays in memory. */
      }
    }
  };
  // Fix 3 (Soak #2): when THIS card becomes the hash target (a jump link
  // like #risk-flags), force it OPEN so the scroll never lands on a
  // collapsed header — a safety card must never hide its rows under a jump.
  // (The persisted collapse is global per card-type, so it can otherwise be
  // closed from a prior visit.) The user can still re-collapse afterward.
  // Keyed on location.key too, so RE-CLICKING the same jump pill (a new
  // history entry, unchanged hash) re-opens a card the CM re-collapsed.
  useEffect(() => {
    if (id === undefined || hash !== `#${id}`) return;
    openCard();
    // (openCard is deliberately not a dependency: it is behaviorally
    // stable — a setState + a storage write — and the seek identity is
    // the navigation, not the closure.)
  }, [hash, locationKey, id, key]);
  useEffect(() => {
    if (archivedShown) setOpen(true);
  }, [archivedShown]);
  const toggle = (): void =>
    setOpen((v) => {
      const next = !v;
      if (key !== null) {
        try {
          window.localStorage.setItem(key, next ? 'open' : 'closed');
        } catch {
          /* localStorage unavailable (private mode) — state stays in memory. */
        }
      }
      return next;
    });
  return { open, toggle, openCard };
}

// ---------------------------------------------------------------------
// CardOpenContext — the open-on-add seam: ProjectionCard (and the
// hand-rolled AllergiesCard) provide their own force-open so an
// AddRowAction rendered in the header can reveal the body after a
// successful add — a direct add on a COLLAPSED card must never leave
// the new row invisible. Null outside a collapsible card.
// ---------------------------------------------------------------------
export const CardOpenContext = createContext<(() => void) | null>(null);

export function ProjectionCard({
  title,
  emptyLabel = "Once your notes confirm something, you'll see it here.",
  action,
  flow = 'list',
  id,
  defaultCollapsed = false,
  collapsedCount,
  archivedShown = false,
  accent,
  children,
  testId,
}: ProjectionCardProps): ReactNode {
  const hasRows = Array.isArray(children) ? children.length > 0 : Boolean(children);
  const pulsing = useConfirmLandedPulse(testId);
  // Hooks run unconditionally, ABOVE the empty-state early return (the
  // React #310 discipline) — the empty branch simply ignores them.
  const { open, toggle, openCard } = usePersistedCardOpen(testId, id, defaultCollapsed, archivedShown);
  const bodyId = useId();

  // Jump-strip landing room: the anchored cards scroll with block:'start'
  // (a card taller than the viewport must land header-first), so the
  // section carries scroll-margin clearing the sticky header + strip
  // (60/68px header + ~38px strip).
  const SCROLL_MT = ' scroll-mt-[100px] md:scroll-mt-[110px]';

  // EMPTINESS IS QUIET (design review U2, adopted): a zero-item section
  // does not earn a full card. It keeps its card on the record (LP-29,
  // owner ruling 2026-10-24: every card lives on the record showing as no
  // entries, one rule for all): the heading, one quiet line, and the
  // card's own doors. The line wraps and is never clipped: on a narrow
  // screen it takes its own row under the heading.
  if (!hasRows) {
    return (
      <section
        id={id}
        data-testid={testId}
        data-empty="true"
        className={
          'rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] px-4 py-2.5' +
          SCROLL_MT +
          (accent ? ` border-l-4 ${accent.band}` : '')
        }
      >
        {/* S3.5: same wrap treatment as the full-card header. */}
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <h3 className={`font-display text-sm shrink-0 ${accent ? accent.heading : 'text-ink-soft'}`}>
            {title}
          </h3>
          <p data-testid="card-empty-line" className="basis-64 min-w-0 flex-1 text-xs italic text-ink-soft">
            {emptyLabel}
          </p>
          {/* The open-on-add seam, as on the full card: the first add on a
              card a person once collapsed must arrive OPEN, showing the
              row just recorded, never under the fold. */}
          {action && (
            <span className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-y-1">
              <CardOpenContext.Provider value={openCard}>{action}</CardOpenContext.Provider>
            </span>
          )}
        </div>
      </section>
    );
  }

  return (
    <section
      id={id}
      data-testid={testId}
      data-pulsing={pulsing ? 'true' : undefined}
      className={
        'rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-4' +
        SCROLL_MT +
        (accent ? ` border-l-4 ${accent.band}` : '') +
        (pulsing ? ' animate-dest-pulse motion-reduce:animate-none' : '')
      }
    >
      {/* S3.5 (launch-UX): flex-wrap header + a wrappable action cluster.
          A card with several header actions (medications: Make med list ·
          From a photo · Add) was shrink-0 nowrap — its min-content width
          rode up the grid track and scrolled the PAGE body sideways at
          375px. Actions now fold under the title instead. */}
      <header className="flex flex-wrap items-center gap-2 mb-3">
        {/* G5 (Soak #2): the card header is a disclosure — the chevron
            collapses/expands the body, default open, persisted per card.
            The header (and its "+ Add") stays put when collapsed. */}
        <Button
          variant="ghost"
          size="sm"
          className="!min-h-0 -ml-1 !px-1 !py-0.5"
          data-testid="section-collapse"
          aria-expanded={open}
          // Only reference the body while it is actually mounted (Fix 5):
          // when collapsed the body is not rendered, so aria-controls would
          // dangle at a non-existent id.
          aria-controls={open ? bodyId : undefined}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          icon={
            open ? (
              <ChevronDown size={16} aria-hidden="true" />
            ) : (
              <ChevronRight size={16} aria-hidden="true" />
            )
          }
          onClick={toggle}
        />
        {/* B5 type courage: card headings take the confident display step.
            PART 4 (domain color system): an accented card tints its
            heading in the section's deep hue instead of the plain
            sage-800 — the same hue as its left border band. */}
        <h3 className={`font-display text-xl ${accent ? accent.heading : 'text-sage-800'}`}>{title}</h3>
        {/* Collapsed count (care-record restructure): a collapsed card
            still says HOW MANY records it holds — never hides that
            records exist. Quiet metadata weight, not a badge. */}
        {!open && collapsedCount !== undefined && (
          <span data-testid="collapsed-count" className="text-sm text-ink-soft">
            · {collapsedCount}
          </span>
        )}
        {/* The open-on-add seam: a header add on a COLLAPSED card reveals
            the body on success so the CM sees the row they just added. */}
        {action && (
          <span className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-y-1">
            <CardOpenContext.Provider value={openCard}>{action}</CardOpenContext.Provider>
          </span>
        )}
      </header>
      {open &&
        (flow === 'list' ? (
          <ul id={bodyId} className="flex flex-col gap-2">
            {children}
          </ul>
        ) : (
          <div id={bodyId}>{children}</div>
        ))}
    </section>
  );
}

// ---------------------------------------------------------------------
// AddRowAction — the card-header add affordance (record-add slice; R1:
// an action that writes is a Button). Opens the registry-derived
// RowEditDialog in create mode; success invalidates the aggregate inside
// the dialog (one audited re-read). Providers compose their own dialog
// (the Directory picker flow) via `dialog`.
//
// Design Pass 2 (A1, empty-state law): the heavy primary pill has a
// BUDGET of 1-2 per screen, spent on the sections a CM adds to most —
// so `emphasis` defaults to the QUIET ghost treatment and the anchor
// cards (medications, appointments; contacts, providers on People) opt
// into 'primary'. Same testid, same dialog, same wire behavior.
// ---------------------------------------------------------------------
export function AddRowAction({
  projection,
  title,
  text,
  testId = 'card-add',
  dialog,
  emphasis = 'quiet',
}: {
  /** The add route token (RowEditDialog create mode) — the ADD union,
   *  which includes the add-only projections (hospital-events). Ignored
   *  when a custom `dialog` is supplied. */
  projection?: RecordAddProjection;
  /** The button's name and the dialog's title. Absent with a
   *  `projection`, it is the projection's add title ("Add medication"). */
  title?: string;
  /** The button's visible words. Given, the button shows them with no
   *  icon and no aria-label (the words are its name); absent, it is the
   *  "+ Add" door named by `title`. */
  text?: string;
  /** The button's data-testid. */
  testId?: string;
  /** Custom dialog factory (providers): receives the close handler. */
  dialog?: (close: () => void) => ReactNode;
  /** 'primary' = the heavy pill (budgeted, anchors only); 'quiet' =
   *  the ghost treatment (default). */
  emphasis?: 'primary' | 'quiet';
}): ReactNode {
  const [open, setOpen] = useState(false);
  const name = title ?? (projection !== undefined ? recordDialogTitle(projection, 'create') : 'Add');
  // A read-only viewer (view_all_clinical but not write_clinical_notes)
  // opens the record but sees no clinical-content write affordances — the
  // "+ Add" header door hides so the Care / Life & logistics / People
  // cards read clean, no 403-on-click. Mirrors the backend
  // write_clinical_notes gate on the record-add routes. A SPICE-only
  // viewer holds write_clinical_notes (she posts session notes) but not
  // the care-management or all-clinical width this predicate also
  // requires, so the door hides for her too — she reads the record; the
  // care manager keeps it.
  const canWrite = canWriteClinicalRecord(useTenant().whoami);
  // The open-on-add seam: inside a collapsible card the header provides
  // its force-open; a successful add reveals the body so the new row is
  // never invisible under a collapsed header. Null elsewhere (cluster).
  const openCard = useContext(CardOpenContext);
  if (!canWrite) return null;
  return (
    <>
      <Button
        variant={emphasis === 'primary' ? 'primary' : 'ghost'}
        size="sm"
        icon={text === undefined ? <Plus size={14} aria-hidden="true" /> : undefined}
        data-testid={testId}
        aria-label={text === undefined ? name : undefined}
        onClick={() => setOpen(true)}
      >
        {text ?? 'Add'}
      </Button>
      {open &&
        (dialog ? (
          dialog(() => setOpen(false))
        ) : projection ? (
          <RowEditDialog
            mode="create"
            title={title}
            projection={projection}
            rowId=""
            values={{}}
            onSaved={openCard ?? undefined}
            onClose={() => setOpen(false)}
          />
        ) : null)}
    </>
  );
}

// ---------------------------------------------------------------------
// Archived rows, one door per page (LP-40, owner ruling 2026-10-27).
// Archived rows leave the screen by default. A page that holds cards
// wraps them in ArchivedItemsPage, which ends with ONE quiet line,
// "4 archived items · Show", counting every card's archived rows; Show
// brings each card's archived rows back INSIDE their own card, beneath
// the active listings, in entry (creation) order, each muted with its
// "Archived" chip and Restore. The line does not render at zero, and
// every page load starts hidden. No card carries a reveal of its own.
// Nothing is deleted; nothing nags.
// ---------------------------------------------------------------------

/** Whether the page's archived rows are showing. False outside a page. */
const ArchivedShownContext = createContext(false);
/** Report one card's archived count to its page. A no-op outside one. */
const ArchivedCountContext = createContext<(key: string, count: number) => void>(() => {});

/** Report this card's archived count to the page line and read whether
 *  the page is showing archived rows. Hand-rolled cards (addresses,
 *  rates) call it directly; the projection cards reach it through
 *  useArchivedReveal. */
export function useArchivedItems(count: number): boolean {
  const key = useId();
  const report = useContext(ArchivedCountContext);
  useEffect(() => {
    report(key, count);
    return () => report(key, 0);
  }, [report, key, count]);
  return useContext(ArchivedShownContext);
}

export interface ArchivedReveal<T> {
  active: T[];
  archived: T[];
  /** The page is showing archived rows. */
  revealed: boolean;
  /** The page is showing archived rows AND this card holds some: the
   *  card opens so they are seen. */
  showing: boolean;
}

/** Partition a card's rows and report its archived count to the page.
 *  `archived` keeps the wire order (the aggregate reads ORDER BY
 *  created_at), which IS the ruled entry-date order for the shown block. */
export function useArchivedReveal<T extends { isActive: boolean }>(
  rows: readonly T[],
): ArchivedReveal<T> {
  const active = rows.filter((r) => r.isActive);
  const archived = rows.filter((r) => !r.isActive);
  const revealed = useArchivedItems(archived.length);
  return { active, archived, revealed, showing: revealed && archived.length > 0 };
}

/** "1 archived item" / "4 archived items". */
export function archivedItemsText(count: number): string {
  return count === 1 ? '1 archived item' : `${count} archived items`;
}

/**
 * The page's one archived door: the cards, then the quiet line at the
 * very bottom. Hidden on every page load (state lives here, never in
 * storage); the line hides at zero, and a page that drains to zero
 * starts hidden again.
 */
export function ArchivedItemsPage({ children }: { children: ReactNode }): ReactNode {
  const [counts, setCounts] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [shown, setShown] = useState(false);
  const report = useCallback((key: string, count: number) => {
    setCounts((prev) => {
      if ((prev.get(key) ?? 0) === count) return prev;
      const next = new Map(prev);
      if (count === 0) next.delete(key);
      else next.set(key, count);
      return next;
    });
  }, []);
  let total = 0;
  for (const n of counts.values()) total += n;
  useEffect(() => {
    if (total === 0) setShown(false);
  }, [total]);
  return (
    <ArchivedCountContext.Provider value={report}>
      <ArchivedShownContext.Provider value={shown && total > 0}>{children}</ArchivedShownContext.Provider>
      {total > 0 && (
        <div data-testid="archived-items-line">
          {/* The words start where the page's quiet cross-links do. */}
          <Button
            variant="ghost"
            size="sm"
            className="!min-h-0 !px-1 !py-0.5 text-[12.5px] text-ink-soft"
            data-testid="archived-items-toggle"
            aria-pressed={shown}
            onClick={() => setShown((v) => !v)}
          >
            {`${archivedItemsText(total)} · ${shown ? 'Hide' : 'Show'}`}
          </Button>
        </div>
      )}
    </ArchivedCountContext.Provider>
  );
}

// ---------------------------------------------------------------------
// The per-row frame — provenance chip + status pill slot + edit/archive.
// ---------------------------------------------------------------------
export interface StatusPill {
  label: string;
  variant: ChipVariant;
}

export interface ProjectionRowProps {
  /** The row's soft-archive + provenance envelope (drives muting + the chip). */
  envelope: Pick<RowEnvelope, 'id' | 'isActive'> & {
    provenance: Provenance | DerivedProvenance;
    flagged?: Provenance | null;
  };
  /** The row's primary label (e.g. the medication name). */
  title: ReactNode;
  /** Optional status pill (rendered as a Chip). */
  pill?: StatusPill | null;
  /** The typed body (variant-specific fields). */
  children?: ReactNode;
  /** Live edit/archive wiring. ABSENT = no affordances at all, quiet
   *  hint. PRESENT with `values`/`label` = Edit + Archive both render.
   *  PRESENT with only `projection` (no direct-edit route) = Archive/
   *  Restore renders, Edit does not. */
  edit?: RowEditConfig | null;
  /** Tooltip copy for the read-only hint (rows without `edit`). */
  readOnlyHint?: string;
  /** A later appearance of a row the card lists under a second heading
   *  (a medication taken at two times of day): the same content, display
   *  only. It carries `data-repeat-of` in place of `data-row-id`, and no
   *  edit, archive or hint, so the row that edits is the first one. */
  repeat?: boolean;
}

const DEFAULT_READONLY_HINT =
  'Change this through a note; the record follows.';

/** The quiet no-affordance marker for rows without a direct-edit route. */
function ReadOnlyHint({ hint }: { hint: string }): ReactNode {
  return (
    <span
      data-testid="row-readonly-hint"
      title={hint}
      className="ml-auto shrink-0 self-start cursor-default text-[11px] italic text-ink-soft"
    >
      via notes
    </span>
  );
}

/**
 * The LIVE inline-edit + archive affordances every editable row inherits
 * (one shared node — identical across variants, never hand-rolled).
 * Edit mounts the registry-derived RowEditDialog; archive/restore PATCH
 * the #408 route directly and refetch the aggregate on success.
 * Exported for the functional-status table (same wiring, table cell).
 */
export function RowActions({
  edit,
  rowId,
  isActive,
  archivable,
}: {
  edit: RowEditConfig;
  rowId: string;
  isActive: boolean;
  archivable: boolean;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // The error copy's contact, from the shell's cached tenant config.
  const contact = useSupportContact({ enabled: false });
  // The per-row Edit / Archive affordances are clinical-record writes (the
  // #408 direct-edit routes gate on write_clinical_notes, and this
  // predicate narrows that to viewers who keep the record). A viewer
  // without it sees no row actions — the row reads clean, no
  // 403-on-click. Hook stays above the early return (#310).
  const canWrite = canWriteClinicalRecord(useTenant().whoami);
  const muted = !isActive;

  // The row's projection travels with the press, not from the render.
  const archiveMut = useMutation({
    mutationFn: (v: { archived: boolean; projection: typeof edit.projection }) =>
      patchRecordRow(tenantSlug, clientId, v.projection, rowId, {
        action: v.archived ? 'archive' : 'restore',
      }),
    onSuccess: async () => {
      setActionError(null);
      // ONE audited re-read of the aggregate after any successful write.
      await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
    },
    onError: (err) => setActionError(recordEditErrorMessage(err, null, contact)),
  });

  if (!canWrite) return null;

  // AT REST (LP-40, owner ruling 2026-10-27; replaces design review U4's
  // hover-to-show): Edit and Archive show on every row a seat can write,
  // without a click on the row first, in the same place on every row, at
  // the quiet ghost weight.
  return (
    <div data-testid="row-actions" className="ml-auto flex max-w-full flex-col items-end gap-1 shrink-0">
      <div className="flex flex-wrap items-center justify-end gap-1">
        {hasEditRoute(edit) && (
          <Button
            variant="ghost"
            size="sm"
            icon={<Pencil size={14} aria-hidden="true" />}
            aria-label="Edit"
            data-testid="row-edit"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
        {archivable && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="row-archive"
            loading={archiveMut.isPending}
            onClick={() => archiveMut.mutate({ archived: !muted, projection: edit.projection })}
          >
            {/* The word, no icon — nothing here is deleted (soft-archive). */}
            {muted ? 'Restore' : 'Archive'}
          </Button>
        )}
      </div>
      {actionError !== null && (
        <p
          role="alert"
          data-testid="row-action-error"
          className="max-w-[220px] text-right text-[11px] text-pepper"
        >
          {actionError}
        </p>
      )}
      {editing && hasEditRoute(edit) && (
        <RowEditDialog
          projection={edit.projection}
          rowId={rowId}
          values={edit.values}
          intro={edit.intro}
          introLink={edit.introLink}
          placeholders={edit.placeholders}
          fieldHelp={edit.fieldHelp}
          emptyValue={edit.emptyValue}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

/** True when an interaction landed on a control (never toggle reveal for
 *  clicks that are themselves actions/navigation). */
export function isControlClick(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('button, a, input, select, textarea, [role="dialog"]') !== null
  );
}

export function ProjectionRow({
  envelope,
  title,
  pill,
  children,
  edit,
  readOnlyHint,
  repeat = false,
}: ProjectionRowProps): ReactNode {
  const muted = !envelope.isActive;
  return (
    <li
      data-testid={repeat ? 'projection-row-repeat' : 'projection-row'}
      data-row-id={repeat ? undefined : envelope.id}
      data-repeat-of={repeat ? envelope.id : undefined}
      data-archived={muted ? 'true' : 'false'}
      className={'rounded-base border border-sage-800/[6%] px-3 py-2 ' + (muted ? 'opacity-60' : '')}
    >
      {/* The row wraps: the title column keeps at least 12rem, and on a
          narrow card the actions drop beneath it, on the right, instead of
          squeezing it; wide cards read as one line, as before. */}
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0 flex-[1_1_12rem]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="min-w-0 text-sm font-semibold text-sage-800 [overflow-wrap:anywhere]">{title}</span>
            {pill && <Chip variant={pill.variant}>{pill.label}</Chip>}
            {muted && <Chip variant="neutral">Archived</Chip>}
          </div>
          {children && <div className="mt-1 text-sm text-ink-soft">{children}</div>}
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <ProvenanceChip provenance={envelope.provenance} />
            {envelope.flagged && (
              <span
                data-testid="flagged-marker"
                className="inline-flex items-center gap-1 rounded-tight bg-gold-soft px-2 py-0.5 text-[11px] font-medium text-caution-deep"
              >
                discrepancy
              </span>
            )}
          </div>
        </div>
        {repeat ? null : edit ? (
          <RowActions
            edit={edit}
            rowId={envelope.id}
            isActive={envelope.isActive}
            archivable
          />
        ) : (
          <ReadOnlyHint hint={readOnlyHint ?? DEFAULT_READONLY_HINT} />
        )}
      </div>
    </li>
  );
}

// (SingleValueRow — the spine's single-value mode for the old housing
//  column — was retired at 0148 / D9 when housing became a multi-row
//  projection rendered through ProjectionRow like every other card.)

// ---------------------------------------------------------------------
// A tiny field-list helper for typed bodies (label: value pairs), so each
// variant renders its fields consistently.
// ---------------------------------------------------------------------
export function FieldList({
  fields,
}: {
  fields: { label: string; value: ReactNode }[];
}): ReactNode {
  const present = fields.filter((f) => f.value !== undefined && f.value !== null && f.value !== '');
  if (present.length === 0) return null;
  return (
    <dl className="flex min-w-0 flex-wrap gap-x-4 gap-y-0.5">
      {present.map((f) => (
        <div key={f.label} className="flex min-w-0 flex-wrap gap-x-1">
          <dt className="text-ink-soft">{f.label}:</dt>
          <dd className="min-w-0 whitespace-pre-wrap text-sage-800 [overflow-wrap:anywhere]">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}
